import React, { useEffect, useState } from 'react';
import {
    Typography, Table, Button, Empty, Select, DatePicker, Space, Row, Col
} from 'antd';
import {
    EyeOutlined, DownloadOutlined,
    CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
    FilterOutlined, ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ApiClient from "../api";
import AssessmentModal from "../components/AssessmentModal";

const api_client = new ApiClient();
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

/* ── Status badge ─────────────────────────────────────────── */
const StatusTag = ({ status }) => {
    const map = {
        ENDED:   { color: '#79A7A5', bg: 'rgba(121,167,165,0.1)', icon: <CheckCircleOutlined />, label: 'Completed' },
        RUNNING: { color: '#f0a500', bg: 'rgba(240,165,0,0.1)',   icon: <SyncOutlined spin />,   label: 'Running'   },
        FAILED:  { color: '#ff4d4f', bg: 'rgba(255,77,79,0.1)',   icon: <CloseCircleOutlined />, label: 'Failed'    },
    };
    const cfg = map[status] || { color: '#6B8585', bg: 'rgba(107,133,133,0.1)', icon: null, label: status };

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.color}33`,
            borderRadius: 6, padding: '2px 10px', fontSize: 11,
            fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.5px',
        }}>
            {cfg.icon} {cfg.label}
        </span>
    );
};

/* ── Amount cell ──────────────────────────────────────────── */
const AmountCell = ({ value, highlightNonZero }) => {
    const isEmpty = value === null || value === undefined || value === 0;
    const color = isEmpty
        ? '#3D5454'
        : highlightNonZero
            ? '#ff4d4f'
            : '#F0F8F7';

    return (
        <Text style={{ color, fontFamily: 'monospace', fontSize: 12 }}>
            {isEmpty
                ? '—'
                : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }
        </Text>
    );
};

/* ── Main component ────────────────────────────────────────── */
const AllTasks = ({wsMessages}) => {
    const today     = dayjs();
    const yesterday = dayjs().subtract(1, 'day');

    const [tasks,     setTasks]     = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selected,  setSelected]  = useState(null);

    // Filter state
    const [dateRange, setDateRange] = useState([yesterday, today]);
    const [status,    setStatus]    = useState('ANY');

    const fetchTasks = async (range = dateRange, st = status) => {
        setLoading(true);
        try {
            const [start, end] = range;
            const user_access = JSON.parse(localStorage.getItem('user_access'));
            const data = await api_client.get_all_cases_ai(
                start.startOf('day').toISOString(),
                end.endOf('day').toISOString(),
                st === 'ANY' ? null : st,
                user_access.roletype,
                user_access.license,
                user_access.license
            );
            setTasks(data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // When a new WS message arrives — re-fetch
    useEffect(() => {
        fetchTasks();
    }, [wsMessages]);

    const handleView = async (record) => {
        const ai_assessment = await api_client.getaiassessment(record.stack_id);
        setSelected({ ...record, assessment_data: ai_assessment?.assessment_data });
        setModalOpen(true);
    };

    const handleApplyFilters = () => {
        fetchTasks(dateRange, status);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Tasks');

        // 1. Define Columns with initial keys
        const columns = [
            { header: 'INSURER', key: 'insurer' },
            { header: 'POLICY', key: 'policy' },
            { header: 'IP NO', key: 'ip_no' },
            { header: 'CREATED', key: 'created' },
            { header: 'CLAIMED AMOUNT', key: 'claimed' },
            { header: 'DISALLOWED AMOUNT', key: 'disallowed' },
            { header: 'STATUS', key: 'status' },
        ];
        worksheet.columns = columns;

        // 2. Add Data
        tasks.forEach(t => {
            worksheet.addRow({
                insurer: t.selected_insurer ?? '',
                policy: t.selected_policy ?? '',
                ip_no: t.ip_no ?? '',
                created: t.created_at ? dayjs(t.created_at).format('DD MMM YY, HH:mm') : '',
                claimed: t.claimed_amount ?? 0,
                disallowed: t.disallowed_amount ?? 0,
                status: t.status ?? '',
            });
        });

        // ── 3. AUTOSIZE LOGIC ──
        worksheet.columns.forEach(column => {
            let maxColumnLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const cellValue = cell.value ? cell.value.toString() : '';
                maxColumnLength = Math.max(maxColumnLength, cellValue.length);
            });
            // Add a little extra padding (e.g., 2 units) so it's not cramped
            column.width = maxColumnLength < 10 ? 10 : maxColumnLength + 2;
        });

        // 4. Freeze the First Row
        worksheet.views = [{ state: 'frozen', ySplit: 1 }];

        // 5. Style the Header Row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.value = cell.value?.toString().toUpperCase();
            cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4A4A' } };
            cell.alignment = { horizontal: 'center' };
        });

        // 6. Style Data Rows
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            row.eachCell((cell, colNumber) => {
                if (rowNumber % 2 === 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F8F7' } };
                }
                // Right-align currency columns
                if (colNumber === 5 || colNumber === 6) {
                    cell.alignment = { horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();

        // Extract start and end dates from your state
        const [start, end] = dateRange;

        // Format: "12 December 2025"
        const dateFrom = start.format('DD MMMM YYYY');
        const dateTo = end.format('DD MMMM YYYY');

        // Create the descriptive filename
        // Result example: audidox_tasks_12 December 2025_to_15 December 2025.xlsx
        const filename = `audidox_tasks_${dateFrom}_to_${dateTo}.xlsx`;

        saveAs(new Blob([buffer]), filename);
    };

    /* ── Table columns ── */
    const columns = [
        {
            title: 'Insurer',
            dataIndex: 'selected_insurer',
            key: 'selected_insurer',
            render: (v) => <Text style={{ color: '#F0F8F7', fontSize: 13 }}>{v}</Text>,
        },
        {
            title: 'Policy',
            dataIndex: 'selected_policy',
            key: 'selected_policy',
            render: (v) => (
                <span style={{
                    fontFamily: 'monospace', color: '#79A7A5', fontSize: 12,
                    background: 'rgba(121,167,165,0.06)', border: '1px solid rgba(121,167,165,0.15)',
                    borderRadius: 4, padding: '2px 8px',
                }}>
                    {v}
                </span>
            ),
        },
        {
            title: 'IP No',
            dataIndex: 'ip_no',
            key: 'ip_no',
            render: (v) => <Text style={{ color: '#9DBDBB', fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>,
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (v) => (
                <Text style={{ color: '#6B8585', fontFamily: 'monospace', fontSize: 12 }}>
                    {v ? dayjs(v).format('DD MMM YY, HH:mm') : '—'}
                </Text>
            ),
        },
        {
            title: 'Claimed',
            dataIndex: 'claimed_amount',
            key: 'claimed_amount',
            render: (v) => <AmountCell value={v} highlightNonZero={false} />,
        },
        {
            title: 'Disallowed',
            dataIndex: 'disallowed_amount',
            key: 'disallowed_amount',
            render: (v) => <AmountCell value={v} highlightNonZero={true} />,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (v) => <StatusTag status={v} />,
        },
        {
            title: '',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                    style={{
                        background: 'transparent',
                        border: '1px solid rgba(121,167,165,0.25)',
                        color: '#79A7A5',
                        borderRadius: 6,
                        fontSize: 12,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <div style={{ maxWidth: 1080 }}>
            <Title level={2} style={{ color: 'var(--text-primary)', fontFamily: 'Space Grotesk', marginBottom: 4 }}>
                All Tasks
            </Title>
            <Text style={{ color: '#3D5454', fontSize: 13, display: 'block', marginBottom: 24 }}>
                Browse all adjudication tasks filtered by date range and status
            </Text>

            {/* ── Filter bar ── */}
            <div style={{
                background: '#161C1C',
                border: '1px solid rgba(121,167,165,0.12)',
                borderRadius: 10,
                padding: '14px 18px',
                marginBottom: 20,
            }}>
                <Row gutter={12} align="middle">
                    <Col>
                        <Text style={{
                            color: '#3D5454', fontSize: 11, textTransform: 'uppercase',
                            letterSpacing: '0.8px', fontFamily: 'monospace', display: 'block', marginBottom: 6,
                        }}>
                            Date Range
                        </Text>
                        <RangePicker
                            value={dateRange}
                            onChange={(vals) => {
                                if (!vals) return;
                                setDateRange(vals);
                                fetchTasks(vals, status);
                            }}
                            allowClear={false}
                            style={{ background: '#0D1515', borderColor: 'rgba(121,167,165,0.2)', borderRadius: 6 }}
                            format="DD MMM YYYY"
                        />
                    </Col>

                    <Col>
                        <Text style={{
                            color: '#3D5454', fontSize: 11, textTransform: 'uppercase',
                            letterSpacing: '0.8px', fontFamily: 'monospace', display: 'block', marginBottom: 6,
                        }}>
                            Status
                        </Text>
                        <Select
                            value={status}
                            onChange={(val) => {
                                setStatus(val);
                                fetchTasks(dateRange, val);
                            }}
                            style={{ width: 150 }}
                            dropdownStyle={{ background: '#161C1C', border: '1px solid rgba(121,167,165,0.2)' }}
                        >
                            <Option value="ANY">Any Status</Option>
                            <Option value="ENDED">
                                <Space size={6}>
                                    <CheckCircleOutlined style={{ color: '#79A7A5' }} />
                                    <span style={{ color: '#79A7A5' }}>Completed</span>
                                </Space>
                            </Option>
                            <Option value="RUNNING">
                                <Space size={6}>
                                    <SyncOutlined spin style={{ color: '#f0a500' }} />
                                    <span style={{ color: '#f0a500' }}>Running</span>
                                </Space>
                            </Option>
                            <Option value="FAILED">
                                <Space size={6}>
                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                    <span style={{ color: '#ff4d4f' }}>Failed</span>
                                </Space>
                            </Option>
                        </Select>
                    </Col>

                    <Col style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 22 }}>
                        <Button
                            icon={<FilterOutlined />}
                            onClick={handleApplyFilters}
                            style={{
                                background: 'rgba(121,167,165,0.1)',
                                border: '1px solid rgba(121,167,165,0.3)',
                                color: '#79A7A5',
                                borderRadius: 6,
                            }}
                        >
                            Apply
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchTasks(dateRange, status)}
                            style={{
                                background: 'transparent',
                                border: '1px solid rgba(121,167,165,0.15)',
                                color: '#3D5454',
                                borderRadius: 6,
                            }}
                        />
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportExcel}
                            disabled={!tasks.length}
                            style={{
                                background: tasks.length ? 'rgba(121,167,165,0.1)' : 'transparent',
                                border: `1px solid ${tasks.length ? 'rgba(121,167,165,0.3)' : 'rgba(121,167,165,0.1)'}`,
                                color: tasks.length ? '#79A7A5' : '#3D5454',
                                borderRadius: 6,
                            }}
                        >
                            Export
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* ── Assessment modal ── */}
            <AssessmentModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                caseData={selected}
                onMarkedRead={() => {}}
            />

            {/* ── Table ── */}
            <Table
                dataSource={tasks}
                columns={columns}
                rowKey="id"
                loading={{ spinning: loading, indicator: <SyncOutlined spin style={{ color: '#79A7A5', fontSize: 24 }} /> }}
                locale={{ emptyText: <Empty description={<Text style={{ color: '#3D5454' }}>No tasks found for the selected filters</Text>} /> }}
                pagination={{ pageSize: 15, style: { marginTop: 16 } }}
                style={{ background: 'transparent' }}
                onRow={() => ({
                    style: { cursor: 'default' },
                    onMouseEnter: e => e.currentTarget.style.background = 'rgba(121,167,165,0.04)',
                    onMouseLeave: e => e.currentTarget.style.background = 'transparent',
                })}
            />

            {/* ── Ant Design dark overrides ── */}
            <style>{`
                .ant-table { background: transparent !important; }
                .ant-table-thead > tr > th {
                    background: #161C1C !important;
                    color: #3D5454 !important;
                    border-bottom: 1px solid rgba(121,167,165,0.12) !important;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    font-family: monospace;
                }
                .ant-table-tbody > tr > td {
                    background: transparent !important;
                    border-bottom: 1px solid rgba(121,167,165,0.06) !important;
                    color: #F0F8F7;
                }
                .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
                .ant-pagination-item a { color: #6B8585 !important; }
                .ant-pagination-item-active { border-color: #79A7A5 !important; }
                .ant-pagination-item-active a { color: #79A7A5 !important; }
                .ant-pagination-prev button, .ant-pagination-next button { color: #6B8585 !important; }

                /* DatePicker / Select dark overrides */
                .ant-picker {
                    background: #0D1515 !important;
                    border-color: rgba(121,167,165,0.2) !important;
                    color: #F0F8F7 !important;
                }
                .ant-picker input { color: #F0F8F7 !important; }
                .ant-picker-separator, .ant-picker-suffix { color: #3D5454 !important; }
                .ant-select-selector {
                    background: #0D1515 !important;
                    border-color: rgba(121,167,165,0.2) !important;
                    color: #F0F8F7 !important;
                    border-radius: 6px !important;
                }
                .ant-select-arrow { color: #3D5454 !important; }
                .ant-select-dropdown {
                    background: #161C1C !important;
                    border: 1px solid rgba(121,167,165,0.2) !important;
                }
                .ant-select-item { color: #9DBDBB !important; }
                .ant-select-item-option-selected { background: rgba(121,167,165,0.1) !important; }
                .ant-select-item-option-active { background: rgba(121,167,165,0.06) !important; }

                /* Disabled export button */
                .ant-btn[disabled] { opacity: 0.4 !important; cursor: not-allowed !important; }
            `}</style>
        </div>
    );
};

export default AllTasks;